import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { menuApi } from "../lib/api";
import {toast} from "../hooks/use-toast";
import {
  MapPin,
  Phone,
  Clock,
  Wine,
  UtensilsCrossed,
  ChevronDown,
  AlertCircle
} from "lucide-react";
import { ScrollArea, ScrollBar } from "../components/ui/scroll-area";
import { Skeleton } from "../components/ui/skeleton";
import {Alert, AlertDescription, AlertTitle} from "../components/ui/alert";
import {Button} from "../components/ui/button";

// DLF Logo URL
const DLF_LOGO_URL = "/assets/images/dlf_logo.jpg";

// Background Image URL
const HERO_BG_URL = "/assets/images/bg.jpg";

// Hero Section Component
const HeroSection = () => {
  return (
    <section 
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black"
    >
      {/* Background Image - Black and White */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-50 grayscale"
        style={{
          backgroundImage: `url('${HERO_BG_URL}')`,
        }}
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 hero-gradient" />
      
      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <p className="font-accent text-gray-light text-sm md:text-base mb-6 tracking-[0.3em] uppercase">
            Associazione Dopolavoro Ferroviario
          </p>
        </motion.div>
        
        {/* DLF Logo in Circle */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          className="mb-8"
        >
          <div className="w-44 h-44 md:w-56 md:h-56 lg:w-64 lg:h-64 mx-auto rounded-full border-2 border-white/30 p-2 bg-black/50 backdrop-blur-sm">
            <img 
              src={DLF_LOGO_URL} 
              alt="DLF Logo" 
              className="w-full h-full object-contain rounded-full"
            />
          </div>
        </motion.div>
        
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
          className="text-gray-light text-sm md:text-base max-w-2xl mx-auto mb-10 font-light"
        >
          Un luogo storico per Velletri, riqualificato e rigenerato dall'attività di giovani volontari. 
          Presso la stazione FFSS Velletri.
        </motion.p>
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
        >
          <a
            href="#menu"
            data-testid="explore-menu-btn"
            className="inline-flex items-center gap-2 bg-white text-black font-semibold tracking-wide uppercase text-sm px-8 py-4 rounded-sm hover:bg-gray-200 transition-colors duration-300"
          >
            Esplora il Menu
            <ChevronDown className="w-4 h-4 animate-bounce" />
          </a>
        </motion.div>
      </div>
      
      {/* Scroll indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <div className="w-1 h-2 bg-white rounded-full" />
        </div>
      </motion.div>
    </section>
  );
};

// Navigation Component
const Navigation = ({ categories, activeCategory, onCategoryChange, loading }) => {
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const menuSection = document.getElementById("menu");
      if (menuSection) {
        const rect = menuSection.getBoundingClientRect();
        setIsSticky(rect.top <= 0);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav 
      data-testid="category-nav"
      className={`${isSticky ? "fixed top-0 left-0 right-0 z-50 nav-glass" : ""} py-4`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <ScrollArea className="w-full whitespace-nowrap">
          <div className="flex gap-3 pb-2">
            <button
              data-testid="category-all"
              onClick={() => onCategoryChange(null)}
              className={`category-pill px-5 py-2 text-sm uppercase tracking-widest border border-gray-dark rounded-sm font-medium ${
                activeCategory === null ? "active" : "text-gray-light hover:text-white"
              }`}
            >
              Tutti
            </button>
            {loading ? (
              <>
                <Skeleton className="h-10 w-24 rounded-sm bg-gray-dark" />
                <Skeleton className="h-10 w-28 rounded-sm bg-gray-dark" />
                <Skeleton className="h-10 w-20 rounded-sm bg-gray-dark" />
              </>
            ) : (
              categories.map((cat) => (
                <button
                  key={cat.id}
                  data-testid={`category-${cat.id}`}
                  onClick={() => onCategoryChange(cat.id)}
                  className={`category-pill px-5 py-2 text-sm uppercase tracking-widest border border-gray-dark rounded-sm font-medium whitespace-nowrap ${
                    activeCategory === cat.id ? "active" : "text-gray-light hover:text-white"
                  }`}
                >
                  {cat.description}
                </button>
              ))
            )}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>
    </nav>
  );
};

// Menu Item Component
const MenuItem = ({ product, index }) => {
  const price = product.prices?.[0]?.value || 0;
  
  // Strip HTML tags from description
  const cleanDescription = (html) => {
    if (!html) return "";
    return html.replace(/<[^>]*>/g, "").replace(/<!--.*?-->/g, "").trim();
  };

  const description = cleanDescription(product.descriptionExtended) || product.descriptionLabel;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      data-testid={`menu-item-${product.id}`}
      className="menu-item-hover bg-surface border border-gray-dark rounded-sm p-5 flex flex-col"
    >
      <div className="flex justify-between items-start gap-4 mb-2">
        <h3 className="font-heading text-xl md:text-2xl text-white tracking-wide">
          {product.description.toUpperCase()}
        </h3>
        <span className="price-tag text-lg md:text-xl whitespace-nowrap">
          €{price.toFixed(2)}
        </span>
      </div>
      
      {description && (
        <p className="text-gray-light text-sm leading-relaxed flex-1">
          {description}
        </p>
      )}
      
      {product.category && (
        <div className="mt-3 pt-3 border-t border-gray-dark/50">
          <span className="text-xs uppercase tracking-wider text-gray-medium">
            {product.category.description}
          </span>
        </div>
      )}
    </motion.div>
  );
};

// Menu Section Component
const MenuSection = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log("[MenuSection] Fetching menu data...");
        
        const [categoriesData, productsData] = await Promise.all([
          menuApi.getCategories(),
          menuApi.getAllProducts(),
        ]);

        console.log("[MenuSection] Data fetched successfully", {
          categoriesCount: categoriesData.categories?.length || 0,
          productsCount: productsData.products?.length || 0,
        });
        
        // Filter categories that have products and are relevant
        const relevantCategories = categoriesData.categories.filter(
          cat => cat.enableForSelfOrderMenu
        );
        
        setCategories(relevantCategories);
        setProducts(productsData.products || []);

        // Show success toast on first load or after retry
        if (retryCount > 0) {
          toast({
            title: "✓ Dati caricati",
            description: "Il menu è stato caricato con successo.",
            duration: 3000,
          });
        }
      } catch (error) {
        console.error("[MenuSection] Error fetching menu data:", error);
        setError(error);

        // Show error toast
        toast({
          title: "Errore nel caricamento",
          description: error.message || "Impossibile caricare il menu. Riprova più tardi.",
          variant: "destructive",
          duration: 5000,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [retryCount]);

  /**
   * Retry fetching data
   */
  const handleRetry = () => {
    console.log("[MenuSection] Retrying data fetch...");
    setRetryCount((prev) => prev + 1);
  };

  const filteredProducts = useMemo(() => {
    if (!activeCategory) return products;
    return products.filter(p => p.idCategory === activeCategory);
  }, [products, activeCategory]);

  const handleCategoryChange = async (categoryId) => {
    setActiveCategory(categoryId);
  };

  return (
    <section id="menu" data-testid="menu-section" className="py-24 md:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="font-accent text-gray-light text-xs uppercase tracking-[0.2em] mb-3">
              La nostra selezione
            </p>
            <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl tracking-wide text-white">
              IL MENU
            </h2>
          </motion.div>
        </div>

        {/* Category Navigation */}
        <Navigation 
          categories={categories} 
          activeCategory={activeCategory} 
          onCategoryChange={handleCategoryChange}
          loading={loading}
        />

        {/* Divider */}
        <div className="section-divider my-8" />

        {/* Error State */}
        {error && !loading && (
            <Alert variant="destructive" className="bg-surface border-red-900/50 mb-8">
              <AlertCircle className="h-5 w-5"/>
              <AlertTitle className="font-heading tracking-wide">
                ERRORE NEL CARICAMENTO
              </AlertTitle>
              <AlertDescription className="space-y-3">
                <p className="text-gray-light">
                  {error.message || "Impossibile caricare il menu. Riprova più tardi."}
                </p>
                <div className="flex gap-3">
                  <Button
                      onClick={handleRetry}
                      size="sm"
                      className="bg-white text-black hover:bg-gray-200"
                  >
                    Riprova
                  </Button>
                  {error.isNetworkError && (
                      <p className="text-xs text-gray-medium self-center">
                        Verifica la tua connessione internet
                      </p>
                  )}
                </div>
              </AlertDescription>
            </Alert>
        )}

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(9)].map((_, i) => (
              <div key={i} className="bg-surface border border-gray-dark rounded-sm p-5">
                <div className="flex justify-between mb-3">
                  <Skeleton className="h-6 w-32 bg-gray-dark" />
                  <Skeleton className="h-6 w-16 bg-gray-dark" />
                </div>
                <Skeleton className="h-4 w-full mb-2 bg-gray-dark" />
                <Skeleton className="h-4 w-3/4 bg-gray-dark" />
              </div>
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-16">
            <UtensilsCrossed className="w-12 h-12 text-gray-dark mx-auto mb-4" />
            <p className="text-gray-light">Nessun prodotto trovato in questa categoria.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {filteredProducts.map((product, index) => (
                <MenuItem key={product.id} product={product} index={index} />
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Product count */}
        {!loading && (
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center text-gray-medium text-sm mt-10"
          >
            {filteredProducts.length} {filteredProducts.length === 1 ? "prodotto" : "prodotti"} disponibili
          </motion.p>
        )}
      </div>
    </section>
  );
};

// Contact Section Component
const ContactSection = () => {
  const contactInfo = [
    {
      icon: MapPin,
      title: "Indirizzo",
      content: "Stazione FFSS Velletri",
      subtitle: "Velletri (RM)",
    },
    {
      icon: Phone,
      title: "Contatti",
      content: "Scrivici in privato",
      subtitle: "o passa a trovarci!",
    },
    {
      icon: Clock,
      title: "Orari",
      content: "Mar - Dom: 18:30 - 02:00",
      subtitle: "Lunedì chiuso",
    },
  ];

  const activities = [
    "Luogo di aggregazione",
    "Bar-Pub",
    "Cucina popolare", 
    "Presentazione libri",
    "Mostre e allestimenti",
    "Mercati dal basso",
    "Cineforum",
    "Attività sportive",
    "Escursioni sul territorio",
    "Musica live",
    "Scuola di Biliardo",
    "Palestra Popolare",
    "Assemblee pubbliche"
  ];

  return (
    <section id="contatti" data-testid="contact-section" className="py-24 md:py-32 bg-surface">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="font-accent text-gray-light text-xs uppercase tracking-[0.2em] mb-3">
            Vieni a trovarci
          </p>
          <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl tracking-wide text-white">
            CONTATTI & ORARI
          </h2>
        </motion.div>

        {/* Contact Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {contactInfo.map((info, index) => (
            <motion.div
              key={info.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              data-testid={`contact-card-${info.title.toLowerCase()}`}
              className="contact-card bg-background border border-gray-dark rounded-sm p-8"
            >
              <info.icon className="w-8 h-8 text-white mb-6" />
              <h3 className="font-heading text-2xl tracking-wide text-white mb-2">
                {info.title.toUpperCase()}
              </h3>
              <p className="text-white text-lg mb-1">{info.content}</p>
              <p className="text-gray-light text-sm">{info.subtitle}</p>
            </motion.div>
          ))}
        </div>

        {/* About the Association */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 p-8 md:p-12 bg-background border border-gray-dark rounded-sm"
        >
          <div className="flex flex-col md:flex-row items-start gap-6">
            <Wine className="w-10 h-10 text-white flex-shrink-0 hidden md:block" />
            <div className="flex-1">
              <h3 className="font-heading text-3xl md:text-4xl tracking-wide text-white mb-4">
                DOPOLAVORO FERROVIARIO
              </h3>
              <p className="text-gray-light leading-relaxed mb-6">
                Un luogo storico per Velletri, riqualificato e rigenerato dall'attività di giovani volontari 
                che operano sul territorio attraverso l'associazione culturale R/Esistenza e la collaborazione 
                di molte altre associazioni. Un luogo d'incontro polifunzionale per tutte le età e tutti i gusti, 
                nei locali del DopoLavoro Ferroviario, presso stazione FFSS Velletri.
              </p>
              
              {/* Activities Grid */}
              <div className="mb-8">
                <h4 className="font-heading text-xl tracking-wide text-white mb-4">ATTIVITÀ PRINCIPALI</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {activities.map((activity, index) => (
                    <span key={index} className="text-gray-light text-sm py-1">
                      #{activity}
                    </span>
                  ))}
                </div>
              </div>

              {/* Call to action */}
              <div className="p-4 bg-surface border border-gray-dark rounded-sm">
                <p className="text-white font-medium mb-1">
                  Vuoi presentare idee, proposte e conoscere meglio questa realtà?
                </p>
                <p className="text-gray-light text-sm">
                  Passa a trovarci l'ultimo lunedì di ogni mese alle 21:00 o scrivici in privato!
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer data-testid="footer" className="py-12 border-t border-gray-dark bg-background">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="text-center md:text-left flex items-center gap-4">
            <img src={DLF_LOGO_URL} alt="DLF" className="w-12 h-12 object-contain" />
            <div>
              <h3 className="font-heading text-xl font-bold text-white">DLF</h3>
              <p className="text-gray-light text-sm">
                Dopolavoro Ferroviario
              </p>
            </div>
          </div>
          
          <div className="flex gap-8">
            <a href="#menu" className="text-gray-light hover:text-white transition-colors duration-300 text-sm uppercase tracking-wider">
              Menu
            </a>
            <a href="#contatti" className="text-gray-light hover:text-white transition-colors duration-300 text-sm uppercase tracking-wider">
              Contatti
            </a>
          </div>
          
          <p className="text-gray-medium text-xs">
            © {new Date().getFullYear()} DLF. Tutti i diritti riservati.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main HomePage Component
const HomePage = () => {
  return (
    <main data-testid="home-page">
      <HeroSection />
      <MenuSection />
      <ContactSection />
      <Footer />
    </main>
  );
};

export default HomePage;
